// Google Analytics 4 Event Tracking Utility
// This file provides helper functions to track custom events in GA4

/**
 * Track a custom event in Google Analytics 4
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track form submissions
 * @param {string} formType - Type of form (contact, booking)
 * @param {object} formData - Form data (sanitized, no PII)
 */
export const trackFormSubmission = (formType, formData = {}) => {
  trackEvent('form_submission', {
    form_type: formType,
    ...formData
  });
};

/**
 * Track calculator usage
 * @param {string} calculatorType - Type of calculator used
 * @param {object} calculationData - Calculation parameters
 */
export const trackCalculatorUse = (calculatorType, calculationData = {}) => {
  trackEvent('calculator_use', {
    calculator_type: calculatorType,
    ...calculationData
  });
};

/**
 * Track button clicks
 * @param {string} buttonName - Name/label of the button
 * @param {string} buttonLocation - Location of button on page
 */
export const trackButtonClick = (buttonName, buttonLocation = '') => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation
  });
};

/**
 * Track navigation link clicks
 * @param {string} linkText - Text of the navigation link
 * @param {string} destination - URL destination
 */
export const trackNavigation = (linkText, destination) => {
  trackEvent('navigation_click', {
    link_text: linkText,
    destination: destination
  });
};

/**
 * Track outbound link clicks (external websites)
 * @param {string} url - External URL
 * @param {string} linkType - Type of link (social_media, external, etc)
 */
export const trackOutboundClick = (url, linkType = 'external') => {
  trackEvent('outbound_click', {
    url: url,
    link_type: linkType
  });
};

/**
 * Track page views (manual tracking if needed)
 * @param {string} pagePath - Path of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-EJVB27BHWS', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

/**
 * Track conversions (goals achieved)
 * @param {string} conversionType - Type of conversion
 * @param {object} conversionData - Additional conversion data
 */
export const trackConversion = (conversionType, conversionData = {}) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    ...conversionData
  });
};

/**
 * Track scroll depth
 * @param {number} percentage - Percentage scrolled
 */
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll_depth', {
    percent_scrolled: percentage
  });
};

// Export all tracking functions
export default {
  trackEvent,
  trackFormSubmission,
  trackCalculatorUse,
  trackButtonClick,
  trackNavigation,
  trackOutboundClick,
  trackPageView,
  trackConversion,
  trackScrollDepth
};
